import { ColumnsType, EditIcon } from '@/components/_common';
import ButtonBack from '@/components/_common/ButtonBack';
import {
  InvoiceConsultTecnoSpeedRef,
  ReferentialTranslator,
} from '@/core/domain/invoice/invoice/Invoice.types';
import { useResolveInvoice } from '@/core/domain/invoice/invoice/invoice.hooks';
import { useDisclosure } from '@/hooks';
import { Button, Col, Modal, Row, Table } from 'antd';
import {
  InvoiceOriginReferenceEntity,
  InvoiceSituation,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useCalculateTaxes } from '@/core/domain/invoice/invoices/invoices.hooks';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { InvoiceOriginReference } from '@/core/domain/invoice/invoices/invoices.types';
import { Link } from 'react-router-dom';
import InvoiceDocumentOptionsButton from './InvoiceDocumentOptionsButton';
import { InvoiceSendEmailInternal } from './InternalInvoice/InvoiceSendEmailInternal';
import { InvoiceTransmitInternal } from './InternalInvoice/InvoiceTransmitInternal';
import { InvoiceCancelInternal } from './InternalInvoice/InvoiceCancelInternal';
import { InvoiceConsultInternal } from './InternalInvoice/InvoiceConsultInternal';
import { InvoiceTransmitTecnoSpeed } from './TecnoSpeedInvoice/InvoiceTransmitTecnoSpeed';
import { InvoiceCancelTecnoSpeed } from './TecnoSpeedInvoice/InvoiceCancelTecnoSpeed';
import { InvoiceConsultTecnoSpeed } from './TecnoSpeedInvoice/InvoiceConsultTecnoSpeed';
import { useRef } from 'react';
import { InvoiceMailTecnoSpeed } from './TecnoSpeedInvoice/InvoiceMailTecnoSpeed';
import usePageSearchHistory from '@/hooks/pageSearchHistory';

const InvoiceButtonsRepeated = () => {
  const {
    handleSubmit,
    isLoading,
    invoiceId,
    invoice: { data: invoice },
    invoiceSetting,
    disabled,
    useInternalInvoiceApiNfe,
    useInternalInvoiceApiNfc,
    useInternalInvoiceApiNfs,
    activeNfseNacional,
    isNfe,
    isNfc,
    isNfs,
    isInternalApiIssued,
  } = useInvoiceFormContext();

  const hasPlugNotasId = !!invoice?.plugNotasId;

  const isAuthorized = invoice?.situation === InvoiceSituation.AUTHORIZED;
  const isDenied = invoice?.situation === InvoiceSituation.DENIED;
  const isCanceled = invoice?.situation === InvoiceSituation.CANCELED;
  const isInTyping = invoice?.situation === InvoiceSituation.IN_TYPING;
  const isValidated = invoice?.situation === InvoiceSituation.VALIDATED;
  const isProcessing = invoice?.situation === InvoiceSituation.PROCESSING;
  const isReplaced = invoice?.situation === InvoiceSituation.REPLACED;
  const isInterrupted = invoice?.situation === InvoiceSituation.INTERRUPTED;
  const isProcessingCancellation =
    invoice?.situation === InvoiceSituation.PROCESSING_CANCELLATION;

  const invoiceNotDeleted = !invoice?.deleted;

  const originReference = invoice?.originReference || [];

  const visibleField = {
    [InvoiceType.NFC]: invoiceSetting?.issueNfc,
    [InvoiceType.NFS]: invoiceSetting?.issueNfs,
    [InvoiceType.NFE]: invoiceSetting?.issueNfe,
    [InvoiceType.CFE]: invoiceSetting?.issueCfe,
  };

  const calculateTaxes = useCalculateTaxes();
  const resolveInvoice = useResolveInvoice();
  const [isOsModalVisible, setIsOsModalVisible] = useDisclosure();

  const handlerUrl = (row: InvoiceOriginReference) => {
    switch (row.entity) {
      case InvoiceOriginReferenceEntity.PURCHASE:
        return `/app/movements/purchases/purchasesReleases/${row.entityId}/general`;

      case InvoiceOriginReferenceEntity.WORK_ORDER:
        return `/app/movements/workOrders/${row.workOrderId}/complements/${row.entityId}/generalInfo`;

      case InvoiceOriginReferenceEntity.SALES:
        return `/app/movements/directSale/${row.entityId}/general`;
    }
  };

  const columns: ColumnsType<InvoiceOriginReference> = [
    {
      title: originReference
        ? `Nº ${ReferentialTranslator[originReference[0]?.entity]}`
        : '',
      dataIndex: 'entityCode',
      key: 'entityCode',
    },
    {
      title: 'Ações',
      width: 50,
      align: 'right',
      fixed: 'right',
      render: (entityCode: number, row: InvoiceOriginReference) => {
        return (
          <Link to={handlerUrl(row)} target="_blank">
            <EditIcon />
          </Link>
        );
      },
    },
  ];

  const invoiceConsultRef = useRef<InvoiceConsultTecnoSpeedRef>(null);

  const handleConsultInvoice = () => {
    if (invoiceConsultRef.current) {
      invoiceConsultRef.current.consultInvoice();
    }
  };

  const handleResolveInvoice = async () => {
    try {
      if (invoiceId) {
        Modal.confirm({
          title: 'Sincronizar com a Prefeitura',
          content:
            'A ação de sincronizar com a prefeitura irá enviar uma solicitação para o portal da prefeitura para realizar a sincronização. Este processo pode levar algum tempo.',
          onOk: async () => {
            const response = await resolveInvoice.mutateAsync(
              Number(invoiceId),
            );

            if (response) {
              handleConsultInvoice();
            }
          },
        });
      }
    } catch {}
  };

  const isNfeOrInternalApiNfe = isNfe && useInternalInvoiceApiNfe;
  const isNfcOrInternalApiNfc = isNfc && useInternalInvoiceApiNfc;
  const isNfsOrInternalApiNfs = isNfs && useInternalInvoiceApiNfs;

  const noteTypesToUseFlag =
    isInTyping || isProcessing || isValidated || isInterrupted || isAuthorized;

  const noteTypesToUseIsInternalApiIssued =
    isAuthorized ||
    isCanceled ||
    isDenied ||
    isProcessingCancellation ||
    isReplaced;

  const isApiConditionMet = noteTypesToUseFlag
    ? isNfeOrInternalApiNfe || isNfcOrInternalApiNfc || isNfsOrInternalApiNfs
    : noteTypesToUseIsInternalApiIssued
    ? isInternalApiIssued
    : false;

  const hasValidInvoiceType =
    !!invoice?.invoiceType && visibleField[invoice.invoiceType];
  const isFormStateValid = isInTyping || isValidated;

  const transmitVisible =
    isFormStateValid && hasValidInvoiceType && invoiceNotDeleted;

  const cancelVisible = isAuthorized && invoiceNotDeleted;

  const consultVisible = invoiceNotDeleted && !(isInTyping || isValidated);

  const sendEmailVisible =
    (isAuthorized || isDenied || isCanceled) && invoiceNotDeleted;

  const syncInvoiceVisible = isAuthorized && isNfs && invoiceNotDeleted;

  const { getSearch } = usePageSearchHistory();
  const path = '/app/fiscal/invoice';
  const pathWithSearch = path + getSearch(path);

  return (
    <>
      {calculateTaxes.isLoading && (
        <ModalLoadingOverlay
          messages={[
            'Calculando tributos dos itens da nota...',
            'Validando dados de tributação...',
          ]}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={24} md={12} lg={3}>
          <ButtonBack disabled={disabled} path={pathWithSearch} />
        </Col>

        {originReference?.length > 0 && invoiceNotDeleted && (
          <Col span={24} md={12} lg={5}>
            <Button block onClick={setIsOsModalVisible.open}>
              {`${
                ReferentialTranslator[originReference[0].entity]
              } Referenciada`}
            </Button>
          </Col>
        )}

        {(isInTyping || isValidated) && invoiceNotDeleted && (
          <Col span={24} md={12} lg={5}>
            <Button
              block
              type="primary"
              onClick={async () => {
                try {
                  await handleSubmit();
                  await calculateTaxes.mutateAsync(Number(invoiceId));
                } catch {}
              }}
              loading={isLoading || calculateTaxes.isLoading}
            >
              Calcular tributos
            </Button>
          </Col>
        )}

        {transmitVisible && isApiConditionMet && (
          <Col span={24} md={12} lg={4}>
            <InvoiceTransmitInternal />
          </Col>
        )}

        {transmitVisible && !isApiConditionMet && (
          <Col span={24} md={12} lg={4}>
            <InvoiceTransmitTecnoSpeed />
          </Col>
        )}

        {cancelVisible && isApiConditionMet && (
          <Col span={24} md={12} lg={4}>
            <InvoiceCancelInternal />
          </Col>
        )}

        {cancelVisible && !isApiConditionMet && (
          <Col span={24} md={12} lg={activeNfseNacional ? 5 : 3}>
            <InvoiceCancelTecnoSpeed />
          </Col>
        )}

        {syncInvoiceVisible && (
          <Col span={24} md={12} lg={5}>
            <Button
              danger
              block
              type="primary"
              onClick={handleResolveInvoice}
              disabled={!hasPermissions([Authorities.FISCAL_INVOICE_CANCEL])}
            >
              Sincronizar com Prefeitura
            </Button>
          </Col>
        )}

        {consultVisible && isApiConditionMet && (
          <Col span={24} md={12} lg={5}>
            <Button.Group style={{ width: '100%' }}>
              <InvoiceConsultInternal />
            </Button.Group>
          </Col>
        )}

        {consultVisible && !isApiConditionMet && hasPlugNotasId && (
          <Col span={24} md={12} lg={5}>
            <InvoiceConsultTecnoSpeed ref={invoiceConsultRef} />
          </Col>
        )}

        {sendEmailVisible && isApiConditionMet && (
          <Col span={24} md={12} lg={4}>
            <InvoiceSendEmailInternal />
          </Col>
        )}

        {sendEmailVisible && !isApiConditionMet && (
          <Col span={24} md={12} lg={4}>
            <InvoiceMailTecnoSpeed />
          </Col>
        )}

        <Col span={24} md={12} lg={6}>
          <InvoiceDocumentOptionsButton isApiConditionMet={isApiConditionMet} />
        </Col>
      </Row>

      <Modal
        title={
          originReference
            ? `${
                ReferentialTranslator[originReference[0]?.entity]
              } referente nota fiscal`
            : ''
        }
        open={isOsModalVisible}
        onCancel={setIsOsModalVisible.close}
        okButtonProps={{ hidden: true }}
      >
        <Table
          columns={columns}
          pagination={false}
          dataSource={originReference}
        />
      </Modal>
    </>
  );
};

export default InvoiceButtonsRepeated;
